import React, { useState, useEffect, useCallback } from 'react'
import { DatePicker, Form, Row, Select } from 'antd'
import type { Dayjs } from 'dayjs'
import DatesPicker from '../../components/DatesPickerV2'
import WatermarksPicker from '../../components/WatermarksPicker'
import DepartmentMemberPicker from '../../components/DepartmentMemberPickerV2'
import dayjs from 'dayjs'
import { useAppSelector } from '../../../app/hooks'
import sensors from '../../../utils/sensors'

const { RangePicker } = DatePicker

// 部门类型
interface Department {
  id: string
  num: number
  [key: string]: unknown
}

// 部门用户类型
interface DepartmentUser {
  userID: string
  [key: string]: unknown
}

// 过滤器状态类型
interface FilterState {
  departments: Department[]
  departmentUsers: DepartmentUser[]
  times: [Dayjs, Dayjs]
  watermarks: string[]
}

// 组件 Props 类型
interface FilterControlsProps {
  pickerUserCount: number
  onFilterChange: (params: unknown) => unknown
  title: string
  handleClickReportSA: (action: string, params?: Record<string, unknown>) => void
}

const FilterControls: React.FC<FilterControlsProps> = ({
  pickerUserCount,
  onFilterChange,
  title,
  handleClickReportSA
}) => {
  const [showDatePickerPane, setShowDatePickerPane] = useState(false)
  const [showWatermarkModal, setShowWatermarkModal] = useState(false)
  const [showMemberModal, setShowMemberModal] = useState(false)
  const [filter, setFilter] = useState<FilterState>({
    departments: [],
    departmentUsers: [],
    times: [dayjs().startOf('day'), dayjs().endOf('day')],
    watermarks: []
  })
  const dataStatisticsType =
    title === '水印统计'
      ? 'dataStatisticWatermarkManage'
      : 'dataStatisticStaffManage'

  const userId = useAppSelector((state) =>
    state.main.user?.user_id
  )
  const groupId = useAppSelector((state) => state.group.groupId)

  useEffect(() => {
    triggerFilterChange(filter)
  }, [filter])

  const triggerFilterChange = useCallback(
    (currentFilter: FilterState) => {
      const formattedTimes = currentFilter.times.map((time) =>
        dayjs(time).format('YYYY-MM-DD HH:mm:ss')
      )
      const userIDs = currentFilter.departmentUsers.map(
        (departmentUser) => departmentUser.userID
      )
      const departmentIDList = currentFilter.departments.map(
        (department) => department.id
      )

      const formattedWatermarks = currentFilter.watermarks.map((watermark) => {
        const [baseID, groupID] = watermark.split('+')
        return { watermarkBaseID: baseID, groupWatermarkID: groupID }
      })

      const modifiedFilter = {
        ...currentFilter,
        times: {
          start: formattedTimes[0],
          end: formattedTimes[1]
        },
        userIDs,
        departmentIDList,
        watermarks: formattedWatermarks
      }
      onFilterChange({
        filter: modifiedFilter,
        userId,
        groupId
      })
    },
    [onFilterChange, userId, groupId]
  )

  const handleDatesChange = (startDate: Dayjs, endDate: Dayjs) => {
    setShowDatePickerPane(false)
    const newFilter: FilterState = {
      ...filter,
      times: [startDate, endDate]
    }
    setFilter(newFilter)
    handleClickReportSA('chooseDate')
  }

  const handleWatermarksChange = (selectedWatermarks: string[]) => {
    setShowWatermarkModal(false)
    const ids = selectedWatermarks.map((id) => id.split('+'))
    sensors.track('web_workgroup_filter_watermark_page_click_done', {
      watermarkNum: selectedWatermarks.length,
      groupWatermarkNum: ids.filter((id) => id[1]).length,
      basicWatermarkNum: ids.filter((id) => id[0] !== '0' && id[1] === '')
        .length,
      noWatermarkNum: ids.filter((id) => id[0] === '0').length,
      pageType: dataStatisticsType
    })
    const updatedFilter = {
      ...filter,
      watermarks: selectedWatermarks
    }
    setFilter(updatedFilter)
    handleClickReportSA('chooseWatermark')
  }

  const handleCalcMemberPickedValue = () => {
    const userNumInDepartments = filter.departments
      .map((department) => department.num)
      .reduce((acc, cur) => acc + cur, 0)
    const total = filter.departmentUsers.length + userNumInDepartments
    const hasSelection =
      filter.departmentUsers.length > 0 || filter.departments.length > 0
    if (
      (!hasSelection && total === 0) ||
      total === pickerUserCount ||
      filter.departments.some((department) => department.id === '0')
    ) {
      return `全部成员（${pickerUserCount}人）`
    } else {
      return `已选${total}人`
    }
  }

  const handleMemberPickerOk = (params: { selectedDepartmentUsers: DepartmentUser[]; selectedDepartments: Department[] }) => {
    const { selectedDepartmentUsers, selectedDepartments } = params
    setShowMemberModal(false)
    const newFilter = {
      ...filter,
      departments: selectedDepartments,
      departmentUsers: selectedDepartmentUsers
    }
    setFilter(newFilter)
    handleClickReportSA('choosePhotoMember')
  }

  return (
    <div className="photo-stat-container">
      <div
        className="photo-stat-title"
        onClick={() => {
          const url = new URL(window.location.href)
          url.searchParams.set('dataStatisticsType', dataStatisticsType)
          window.history.pushState({}, '', url.toString())
        }}
      >
        <div className="title">{title}</div>
        <Form layout="inline">
          <Row>
            <Form.Item>
              <div onClick={() => {
                sensors.track('web_workgroup_filter_time_click', {
                  pagePath: dataStatisticsType
                })
              }}>
                <RangePicker
                  value={filter.times}
                  open={false}
                  allowClear={false}
                  onOpenChange={() => setShowDatePickerPane(!showDatePickerPane)}
                  style={{ width: '236px' }}
                />
              </div>
            </Form.Item>
            <Form.Item>
              <Select
                style={{ width: '236px' }}
                value={
                  filter.watermarks.length === 0
                    ? '全部水印类型'
                    : `${filter.watermarks.length}个水印`
                }
                open={false}
                onDropdownVisibleChange={() =>
                  setShowWatermarkModal(!showWatermarkModal)
                }
              />
            </Form.Item>
            <Form.Item>
              <Select
                style={{ width: '236px' }}
                value={handleCalcMemberPickedValue()}
                open={false}
                onDropdownVisibleChange={() =>
                  setShowMemberModal(!showMemberModal)
                }
              />
            </Form.Item>
          </Row>
        </Form>
        <DatesPicker
          visible={showDatePickerPane}
          dates={filter.times}
          allowTime={false}
          maxMonths={1}
          onChange={handleDatesChange}
          onCancel={() => setShowDatePickerPane(false)}
          dataStatisticsType={dataStatisticsType}
        />
        <WatermarksPicker
          visible={showWatermarkModal}
          onOK={handleWatermarksChange}
          onCancel={() => setShowWatermarkModal(false)}
          checkedList={filter.watermarks}
          onlyInGroup={true}
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          fetchWatermarkList={undefined as any}
        />
        <DepartmentMemberPicker
          open={showMemberModal}
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          filterObj={filter as any}
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          onOk={handleMemberPickerOk as any}
          onCancel={() => setShowMemberModal(false)}
          dataStatisticsType={dataStatisticsType}
          onlyInGroup={title === '水印统计' ? false : true}
        />
      </div>
    </div>
  )
}

export default FilterControls
